import React from "react";
import styled, { css } from "styled-components";
import Media from "react-media";
import Gettext from "./common/Gettext";
import { fontSize } from "./common/styled-mixins";
import { Link } from "./common/Link";

import CloseIcon from "./Icons/Close";
import ArrowIcon from "./Icons/SideArrow";
import desktopLightImg from "../images/desktop-light-bonus-indicator.png";
import desktopDarkImg from "../images/desktop-dark-bonus-indicator.png";
import mobileLightImg from "../images/mobile-light-bonus-indicator.png";
import mobileDarkImg from "../images/mobile-dark-bonus-indicator.png";

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 20003;
`;

const Overlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Container = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
  width: 740px;
  padding: 43px 35px 32px;
  color: ${({ theme }) => theme.freeBonusPopupFontColor};
  background-color: ${({ theme }) => theme.popupDarkBackground};
  border-radius: 4px;

  @media (max-width: 959px) {
    top: 0;
    left: 0;
    transform: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 20px 17px;
    border-radius: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 19px;
  right: 19px;
  display: block;
  width: 20px;
  height: 20px;
  padding: none;
  color: #717171;
  background: transparent;
  border: none;
  cursor: pointer;
`;

const TitleContainer = styled.div`
  margin-bottom: 20px;
  text-align: center;

  @media (max-width: 959px) {
    margin-left: 46px;
    margin-right: 46px;
  }
`;

const Title = styled.div`
  line-height: 1.2;
  font-weight: bold;
`;

const LittleTitle = styled(Title)`
  margin-bottom: 10px;
  font-size: 28px;

  @media (max-width: 959px) {
    font-size: ${fontSize(18)};
  }
`;

const BigTitle = styled(Title)`
  font-size: 64px;

  @media (max-width: 959px) {
    font-size: 24px;
  }
`;

const Percent = styled.span`
  color: ${({ theme }) => theme.freeBonusPopupPercentColor};
`;

const Description = styled.p`
  margin-bottom: 33px;
  font-size: ${fontSize(18)};
  line-height: 1.2;
  text-align: center;

  @media (max-width: 959px) {
    margin-bottom: 20px;
    font-size: ${fontSize(12)};
  }
`;

const HintBlock = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 33px;

  @media (max-width: 959px) {
    margin-bottom: 21px;

    @media (orientation: portrait) {
      flex-direction: column;
      align-items: center;
    }
  }
`;

const HintText = styled.p`
  width: 413px;
  margin-top: 18px;
  margin-right: 10px;
  font-size: ${fontSize(14)};
  line-height: 24px;
  color: ${({ theme }) => theme.freeBonusPopupHintColor};

  @media (max-width: 959px) {
    width: auto;
    max-width: 250px;
    font-size: ${fontSize(12)};
    line-height: 1.2;
    margin-top: 9px;

    @media (orientation: portrait) {
      margin-top: 0;
      margin-right: 0;
      margin-bottom: 15px;
    }
  }
`;

const HintImage = styled.div`
  position: relative;
  width: 92px;
  height: 110px;
  ${({ theme }) =>
    theme.mode === "light"
      ? css`
          background-image: url(${desktopLightImg});
        `
      : css`
          background-image: url(${desktopDarkImg});
        `};

  @media (max-width: 959px) {
    width: 209px;
    min-width: 209px;
    height: 81px;

    ${({ theme }) =>
      theme.mode === "light"
        ? css`
            background-image: url(${mobileLightImg});
          `
        : css`
            background-image: url(${mobileDarkImg});
          `};
  }
`;

const HintLink = styled(Link)`
  color: #0085eb;
  text-decoration: underline;
  cursor: pointer;

  :hover {
    text-decoration: none;
  }
`;

const HintImageArrow = styled.div`
  position: absolute;
  left: -27px;
  bottom: 0;
  right: calc(100% - 10px);
  width: 25px;
  height: 16px;
  color: ${({ theme }) => theme.freeBonusPopupArrowColor};
`;

const ButtonBlock = styled.div`
  display: flex;
  justify-content: center;
`;

const Button = styled.button`
  position: relative;
  min-width: 140px;
  padding: 12px 36px;
  font-weight: bold;
  font-size: ${fontSize(12)};
  color: #ffffff;
  text-decoration: none;
  text-transform: uppercase;
  background-image: linear-gradient(to left, #47c0c8 0%, #5c6cc0 100%);
  border: none;
  border-radius: 32px;
  cursor: pointer;
  z-index: 2;

  ::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: block;
    background-image: linear-gradient(to left, #5c6cc0 0%, #47c0c8 100%);
    border-radius: inherit;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.5s;
  }

  :hover::before {
    opacity: 1;
  }
`;

const FreeBonusPopup = props => {
  return (
    <Wrapper>
      <Overlay />
      <Container>
        <CloseButton onClick={props.onClose}>
          <CloseIcon />
        </CloseButton>
        <TitleContainer>
          <LittleTitle>
            <Gettext>Deposit any amount of money now and enjoy</Gettext>
          </LittleTitle>{" "}
          <BigTitle>
            <Gettext>
              a free <Percent>50%</Percent> bonus!
            </Gettext>
          </BigTitle>
        </TitleContainer>
        <Description>
          <Gettext>
            We give this bonus to encourage and support our new traders, so you
            can start your trading career with maximum benefits and minimum
            losses.
          </Gettext>
        </Description>
        <HintBlock>
          <HintText>
            <Gettext>
              Please note that you can use this bonus only for a limited time.
              You can always check the time left
              <HintLink to={`/fillup?promocode=${props.promo}`}>
                <Gettext>here</Gettext>
              </HintLink>
              or near a bonus indicator.
            </Gettext>
          </HintText>
          <HintImage>
            <Media
              query="(min-width: 960px)"
              render={() => (
                <HintImageArrow>
                  <ArrowIcon />
                </HintImageArrow>
              )}
            />
          </HintImage>
        </HintBlock>
        <ButtonBlock>
          <Button onClick={props.onClose}>
            <Gettext>Got it</Gettext>
          </Button>
        </ButtonBlock>
      </Container>
    </Wrapper>
  );
};

export default FreeBonusPopup;



// WEBPACK FOOTER //
// ./src/components/FreeBonusPopup.js